import React, { FC, useContext, useReducer, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { IdxStatus, AuthSdkError, Tokens } from "@okta/okta-auth-js";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import {
  ConfirmationModal,
  Error as ErrorComponent,
  LocaleView,
  TermsAndConditionsModal,
} from "./components";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  TextField,
  Loader,
} from "@repo/ui";
import { ExtendedAccessToken } from "./interfaces";
import { Actions, initialState, reducer, State } from "./state";
import Cookies from "js-cookie";
import {
  ACTIVE,
  EMAILNOTCONFIRMED,
  LOGIN_PAGE_NAME,
  LOCKEDOUT,
  PASSWORDRECOVERY,
  LOCAL_ACCEPTED_TNCS,
  REMEMBER_ME,
  REMEMBER_ME_FLAG,
  REVOKED,
  STEP_1_INSTITUTIONAL_SITE_LOGIN,
  TNC_TENANT,
} from "./constants";
import { AudienceContext } from "@repo/contexts";
import {
  buildQueryString,
  buildHref,
  INSTITUTIONAL_SITE_LOGIN,
  NORTH_AMERICA,
  EUROPE_ASIA,
} from "@repo/utils";
import { stripHtml } from "./utils";
import {
  accountAuthConfig,
  clearOktaAuths,
  getOktaAccountAuthObject,
  getOktaAuthObject,
  getSessionAndTokens,
  institutionalAuthConfig,
} from "@repo/auth-config";

import {
  clickEvent,
  errorMessageEvent,
  loginEvent,
  LoginEventName,
} from "@repo/analytics";
import { getLoginProcessStepLabel } from "@repo/analytics";
import {
  checkTnCAcceptance,
  fetchTnCContent,
  acceptTnC,
  fetchAcceptedTnCs,
} from "./services/termsAndConditions";
import { checkUserStatus } from "./services/userStatus";

const StyledLinkWrapper = styled("div")(() => ({
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  margin: "20px 0 15px",
  "& > a": {
    marginBottom: 9,
  },
}));

const StyledTextFieldWrapper = styled("div")(({ theme }) => ({
  marginTop: 7,
  width: "100%",
  [theme.breakpoints.up("md")]: {
    width: 300,
  },

  "& .MuiTextField-root": {
    marginTop: 28,
  },
}));

const validateFormInputs = (state: State) => {
  return state.idOrUserName && state.password;
};

const LoginForm: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isTouched = useRef(false);
  const [state, dispatch] = useReducer(reducer, initialState);
  const { audience, paths } = useContext(AudienceContext);
  const intl = useIntl();
  const queryParams = buildQueryString(["next", "region"]);
  const redirectUri = buildHref(paths.loginLegacyAuth, queryParams);

  const {
    idOrUserName,
    password,
    enableRememberMe,
    errorMessage,
    isLoading,
    showConfirmationModal,
    showTnCModal,
    tncContent,
    tncVersion,
  } = state;

  useEffect(() => {
    const rememberMeFlag = Cookies.get(REMEMBER_ME_FLAG);
    const rememberMeName = Cookies.get(REMEMBER_ME);
    if (rememberMeFlag === "Y" && rememberMeName) {
      dispatch({
        type: Actions.SET_REMEMBER_ME,
        payload: { enableRememberMe: true, idOrUserName: rememberMeName },
      });
    }
  }, []);

  useEffect(() => {
    if (location.search.includes("confirmation=true")) {
      dispatch({
        type: Actions.SHOW_CONFIRMATION_MODAL,
        payload: true,
      });
    }
  }, [location.search]);

  useEffect(() => {
    const eventName = state.analyticEvent as LoginEventName;
    loginEvent({
      eventName,
      processStepLabel: getLoginProcessStepLabel(audience),
      processStepValue: STEP_1_INSTITUTIONAL_SITE_LOGIN,
      loginMethod: INSTITUTIONAL_SITE_LOGIN,
      siteLoginStatus: "true",
    });

    if (
      eventName === LoginEventName.LoginFailure ||
      eventName === LoginEventName.LoginLockout
    ) {
      errorMessageEvent({
        formFieldError: stripHtml(state.errorMessage),
        processStepLabel: getLoginProcessStepLabel(audience),
        processStepValue: STEP_1_INSTITUTIONAL_SITE_LOGIN,
      });
    }
  }, [state.analyticEvent, state.errorMessage, audience]);

  const handleClose = () => {
    const urlParams = new URLSearchParams(location.search);
    urlParams.delete("confirmation");
    const newUrl = `${location.pathname}?${urlParams.toString()}`;
    navigate(newUrl);
    dispatch({
      type: Actions.SHOW_CONFIRMATION_MODAL,
      payload: false,
    });
  };

  /**
   * Blocks the login flow when required T&C acceptance cannot be completed.
   * "declined" means the user rejected the terms and sees the T&C acceptance
   * message; "system" means acceptance could not be verified or recorded due
   * to a service/system failure and shows the generic system error copy.
   */
  const failTnCEnforcement = (reason: "system" | "declined") => {
    dispatch({
      type: Actions.TNC_ERROR,
      payload: {
        analyticEvent: LoginEventName.LoginFailure,
        msg: intl.formatMessage({
          id: reason === "declined" ? "tncError" : "systemError",
        }),
      },
    });
  };

  /**
   * Tears down any partially established Okta session so a user blocked by the
   * T&C gate is not left authenticated.
   */
  const clearPartialAuthState = async () => {
    try {
      const { institutionalAuth } = await clearOktaAuths();
      institutionalAuth.idx.clearTransactionMeta();
    } catch (error) {
      // Cleanup failures must not mask the T&C enforcement error
      console.error("Failed to clear auth state after T&C failure:", error);
    }
  };

  const performOktaLogin = async (pendingTnCVersion = 0) => {
    const accountAuth = getOktaAccountAuthObject(accountAuthConfig);
    const institutionalAuth = getOktaAuthObject(institutionalAuthConfig);

    try {
      const authenticationFlow = await institutionalAuth.idx.authenticate({
        username: idOrUserName,
        password,
      });

      const { messages, status, tokens } = authenticationFlow;
      const accessToken = tokens?.accessToken as ExtendedAccessToken;
      const partyId = accessToken?.claims?.partyID ?? "";
      const messageKey = messages?.[0]?.i18n?.key;
      let errMessage = intl.formatMessage({ id: "systemError" });

      switch (status) {
        case IdxStatus.SUCCESS: {
          // Deferred T&C acceptance must be recorded before the session is
          // finalized, otherwise a failed request would leave the user logged
          // in without having accepted the terms.
          if (pendingTnCVersion) {
            if (!accessToken?.accessToken) {
              await clearPartialAuthState();
              failTnCEnforcement("system");
              return;
            }

            try {
              await acceptTnC(
                idOrUserName,
                TNC_TENANT,
                pendingTnCVersion,
                accessToken.accessToken,
              );
            } catch {
              await clearPartialAuthState();
              failTnCEnforcement("system");
              return;
            }

            try {
              console.info("Fetching accepted T&C versions");
              const response = await fetchAcceptedTnCs(accessToken.accessToken);
              localStorage.setItem(
                LOCAL_ACCEPTED_TNCS,
                JSON.stringify(response.acceptedTncs),
              );
              console.info("Accepted T&C versions persisted");
            } catch (error) {
              console.warn("Unable to persist accepted T&C versions:", error);
            }
          }

          Cookies.set(REMEMBER_ME_FLAG, enableRememberMe ? "Y" : "N");

          const loginCompletedEvent = {
            event: LoginEventName.LoginComplete,
            process: {
              processStepLabel: getLoginProcessStepLabel(audience),
              processStepValue: `Complete: ${INSTITUTIONAL_SITE_LOGIN}`,
            },
            userStatus: {
              loginMethod: INSTITUTIONAL_SITE_LOGIN,
              siteLoginStatus: "",
            },
          };

          localStorage.setItem(
            "pushAnalyticsEvent",
            JSON.stringify(loginCompletedEvent),
          );

          if (window.localStorage) {
            localStorage.setItem("InstlPartyID", partyId);
          }

          dispatch({
            type: Actions.LOGIN_SUCCESS,
          });

          if (tokens) {
            institutionalAuth.tokenManager.setTokens(tokens as Tokens);

            const accountTokens = await getSessionAndTokens(accountAuth);

            if (accountTokens) {
              await accountAuth.handleLoginRedirect(
                accountTokens.tokens,
                redirectUri,
              );
            } else {
              throw new Error("Unable to get legacy tokens");
            }
          }

          break;
        }
        case IdxStatus.PENDING:
          if (messageKey === "errors.E0000004") {
            errMessage = intl.formatMessage({ id: "invalidCombination" });
          }

          dispatch({
            type: Actions.LOGIN_ERROR,
            payload: {
              analyticEvent: LoginEventName.LoginFailure,
              msg: errMessage,
            },
          });
          break;
        case IdxStatus.TERMINAL: {
          let analyticEvent = LoginEventName.LoginFailure;

          if (messageKey === "oie.selfservice.user.unlock.not.allowed") {
            analyticEvent = LoginEventName.LoginLockout;
            errMessage = intl.formatMessage({ id: "lockout" });
          }

          dispatch({
            type: Actions.LOGIN_ERROR,
            payload: {
              analyticEvent,
              msg: errMessage,
            },
          });
          institutionalAuth.transactionManager.clear();
          break;
        }

        default:
          console.log("Unhandled status:", status);
          dispatch({
            type: Actions.LOGIN_ERROR,
            payload: {
              analyticEvent: LoginEventName.LoginFailure,
              msg: errMessage,
            },
          });
      }
    } catch (error) {
      let errorMessage = intl.formatMessage({ id: "systemError" });
      let event = LoginEventName.LoginFailure;

      if (error instanceof Error) {
        errorMessage = intl.formatMessage({ id: "invalidCombination" });
      }

      if (error instanceof AuthSdkError) {
        errorMessage = intl.formatMessage({ id: "lockout" });
        event = LoginEventName.LoginLockout;
        institutionalAuth.transactionManager.clear();
      }

      dispatch({
        type: Actions.LOGIN_ERROR,
        payload: {
          analyticEvent: event,
          msg: errorMessage,
        },
      });
    }
  };

  const handleTnCAccepted = async () => {
    const acceptedVersion = tncVersion;

    dispatch({ type: Actions.SHOW_TNC_MODAL, payload: false });

    // Without a version the acceptance cannot be recorded, so block the login
    if (!acceptedVersion) {
      failTnCEnforcement("system");
      return;
    }

    dispatch({ type: Actions.LOGIN_STARTED });
    await performOktaLogin(acceptedVersion);
  };

  const handleTnCClose = () => {
    failTnCEnforcement("declined");
  };

  const handleLoginWithIdxFlow = async (event: React.FormEvent) => {
    event.preventDefault();

    // Track interaction on form submission (covers Enter key with pre-populated fields)
    if (!isTouched.current) {
      handleInteractionClick();
      isTouched.current = true;
    }

    const isFormValid = validateFormInputs(state);

    if (!isFormValid) {
      dispatch({
        type: Actions.LOGIN_ERROR,
        payload: {
          analyticEvent: LoginEventName.LoginFailure,
          msg: intl.formatMessage({ id: "wrongCombination" }),
        },
      });

      return;
    }

    try {
      const responseData = await checkUserStatus(idOrUserName, LOGIN_PAGE_NAME);
      const { status: userStatus } = responseData;

      if (userStatus !== ACTIVE) {
        if (userStatus === REVOKED) {
          window.location.replace(paths.accessDenied);
          return;
        }

        let analyticEvent = LoginEventName.LoginFailure;
        let msg = intl.formatMessage({ id: "invalidCombination" });

        switch (userStatus) {
          case EMAILNOTCONFIRMED:
            msg = intl.formatMessage({ id: "completeRegistrationError" });
            break;
          case LOCKEDOUT:
            analyticEvent = LoginEventName.LoginLockout;
            msg = intl.formatMessage({ id: "lockout" });
            break;
          case PASSWORDRECOVERY:
            break;
        }

        dispatch({
          type: Actions.LOGIN_ERROR,
          payload: {
            analyticEvent,
            msg,
          },
        });

        return;
      }

      // begin authentication flow
      dispatch({
        type: Actions.LOGIN_STARTED,
      });

      // T&C gate — North America only
      if (audience === NORTH_AMERICA) {
        try {
          const tncStatus = await checkTnCAcceptance(idOrUserName, TNC_TENANT);

          if (!tncStatus.accepted) {
            const tncData = await fetchTnCContent(TNC_TENANT);
            dispatch({
              type: Actions.SET_TNC_CONTENT,
              payload: { content: tncData.content, version: tncData.version },
            });
            dispatch({ type: Actions.SHOW_TNC_MODAL, payload: true });
            return;
          }
        } catch {
          // Fail closed: acceptance cannot be verified or the terms cannot be
          // presented, so the login must not continue
          failTnCEnforcement("system");
          return;
        }
      }

      await performOktaLogin();
    } catch (error) {
      let errorMessage = intl.formatMessage({ id: "systemError" });
      const event = LoginEventName.LoginFailure;

      // handle status api error
      if (error instanceof Error) {
        errorMessage = intl.formatMessage({ id: "invalidCombination" });
      }

      dispatch({
        type: Actions.LOGIN_ERROR,
        payload: {
          analyticEvent: event,
          msg: errorMessage,
        },
      });
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const action =
      name === "idOrUserName"
        ? Actions.SET_ID_OR_USERNAME
        : Actions.SET_PASSWORD;
    dispatch({
      type: action,
      payload: value.trim(),
    });
  };

  const handleInteractionClick = () => {
    dispatch({
      type: Actions.SET_ANALYTIC_EVENT,
      payload: LoginEventName.LoginStarted,
    });
  };

  const handleInputClick = () => {
    if (isTouched.current) return;
    handleInteractionClick();
    isTouched.current = true;
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: Actions.SET_REMEMBER_ME,
      payload: { enableRememberMe: event.target.checked },
    });
  };

  const handleAnalyticClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    const id = e.currentTarget.id;
    const isForgotUsername = id === "forgot-username";
    const customLinkName = isForgotUsername
      ? `${INSTITUTIONAL_SITE_LOGIN}_Forgot Username`
      : `${INSTITUTIONAL_SITE_LOGIN}_Forgot Password`;
    const linkId = isForgotUsername
      ? intl.formatMessage({ id: "recoverIdLink" })
      : intl.formatMessage({ id: "recoverPswdLink" });
    clickEvent({
      customLinkName,
      linkId,
      linkPosition: "",
    });
  };

  const handleLoginInClick = () => {
    if (!isTouched.current) {
      handleInteractionClick();
      isTouched.current = true;
    }
    clickEvent({
      customLinkName: `${INSTITUTIONAL_SITE_LOGIN}_Login`,
      linkId: intl.formatMessage({ id: "loginButtonText" }),
      linkPosition: "",
    });
  };

  return (
    <>
      {errorMessage && <ErrorComponent {...{ errorMsg: errorMessage }} />}
      <form onSubmit={handleLoginWithIdxFlow}>
        <StyledTextFieldWrapper>
          <TextField
            autoComplete="username"
            autoFocus
            fullWidth
            id="user" // necessary for coveo
            label="id or user name"
            margin="normal"
            name="idOrUserName"
            onChange={handleInputChange}
            onClick={handleInputClick}
            value={idOrUserName}
            variant="standard"
          />
          <TextField
            autoComplete="current-password"
            fullWidth
            id="password" // necessary for coveo
            label="password"
            margin="normal"
            name="password"
            onChange={handleInputChange}
            onClick={handleInputClick}
            type="password"
            value={password}
            variant="standard"
          />
        </StyledTextFieldWrapper>
        <StyledLinkWrapper>
          <LocaleView audience={audience} locales={[NORTH_AMERICA]}>
            <Link
              data-eddl-disabled
              id="forgot-username"
              onClick={handleAnalyticClick}
              variant="body1"
              href={paths.forgotUsername}
            >
              {intl.formatMessage({ id: "recoverIdLink" })}
            </Link>
          </LocaleView>
          <LocaleView
            audience={audience}
            locales={[NORTH_AMERICA, EUROPE_ASIA]}
          >
            <Link
              data-eddl-disabled
              id="forgot-password"
              onClick={handleAnalyticClick}
              variant="body1"
              href={paths.forgotPassword}
            >
              {intl.formatMessage({ id: "recoverPswdLink" })}
            </Link>
          </LocaleView>
        </StyledLinkWrapper>
        <FormControlLabel
          control={
            <Checkbox
              onChange={handleCheckboxChange}
              checked={enableRememberMe}
            />
          }
          label={intl.formatMessage({ id: "checkBoxTxt" })}
          labelPlacement="end"
        />
        <Box sx={{ margin: "18px 0 32px" }}>
          <Button
            disabled={isLoading}
            endIcon={<ArrowForward />}
            id="loginBtn"
            onClick={handleLoginInClick}
            type="submit"
          >
            {intl.formatMessage({ id: "loginButtonText" })}
          </Button>
        </Box>
      </form>
      <ConfirmationModal
        show={showConfirmationModal}
        handleClose={handleClose}
      />
      <LocaleView audience={audience} locales={[NORTH_AMERICA]}>
        <TermsAndConditionsModal
          isOpen={showTnCModal}
          tncContent={tncContent}
          tncVersion={tncVersion}
          onAccept={handleTnCAccepted}
          onClose={handleTnCClose}
        />
      </LocaleView>
      {isLoading && <Loader isFullPage />}
    </>
  );
};

export default LoginForm;
